import type { OrganizationMembership } from '@/types/database'

export interface AuthUser {
  email: string
  name: string
}

/**
 * Fetch the current user's organization memberships from the session-gated BFF
 * proxy (`/api/data/me`). The proxy forwards the verified X-Atsm-Email; the Rust
 * /api/me handler resolves the user and joins user_organizations.
 */
export async function fetchMemberships(): Promise<OrganizationMembership[]> {
  const res = await fetch('/api/data/me', {
    credentials: 'include',
    headers: { accept: 'application/json' },
  })
  if (!res.ok) {
    if (res.status === 401) return []
    throw new Error(`me ${res.status}`)
  }
  const body = (await res.json()) as { memberships?: OrganizationMembership[] } | null
  return body?.memberships ?? []
}

const SESSION_KEY = 'camp_auth_user'

/**
 * Magic-link sign-in is retired. Authentication is handled entirely by the
 * Atsume ID (Zitadel) OIDC BFF flow at `/api/auth/oidc/start`; the session
 * lives in the HttpOnly `camp_session` cookie, surfaced via AuthContext +
 * `/api/auth/me`. This is kept only so existing callers compile; it never sets
 * a user. The Login page should redirect the user into the OIDC flow rather
 * than calling this.
 */
export async function sendMagicLink(_email: string): Promise<{ success: true }> {
  void _email
  return { success: true }
}

export function getCurrentUser(): AuthUser | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY) || sessionStorage.getItem(SESSION_KEY)
    if (!raw) return null
    return JSON.parse(raw) as AuthUser
  } catch {
    return null
  }
}

export function setCurrentUser(user: AuthUser): void {
  localStorage.setItem(SESSION_KEY, JSON.stringify(user))
  sessionStorage.removeItem(SESSION_KEY)
}

/**
 * Clear local identity and the BFF session cookie. No Supabase auth involved —
 * the OIDC session is owned by the BFF (`/api/auth/logout` clears the cookie).
 */
export async function logout(): Promise<void> {
  localStorage.removeItem(SESSION_KEY)
  sessionStorage.removeItem(SESSION_KEY)
  try {
    await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' })
  } catch {
    /* best-effort: cookie clear is also attempted by AuthContext.logout */
  }
}
